import React, { useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { productAPI, categoryAPI } from '../api/services';
import ProductCard from '../components/ProductCard';
import './Products.css';

function buildTree(cats, parentId = null) {
  return cats
    .filter(c => (c.parent_id ?? null) === parentId)
    .map(c => ({ ...c, children: buildTree(cats, c.id) }));
}

function CategoryTree({ nodes, selectedCategory, expandedCats, onSelect, onToggle, depth = 0 }) {
  return (
    <>
      {nodes.map(node => (
        <div key={node.id}>
          <button
            className={`category-btn cat-tree-btn ${selectedCategory === node.id ? 'active' : ''} depth-${Math.min(depth, 2)}`}
            style={{ paddingLeft: 12 + depth * 14 }}
            onClick={() => {
              onSelect(node.id);
              if (node.children.length > 0) onToggle(node.id);
            }}
          >
            {node.children.length > 0 && (
              <span className={`cat-arrow ${expandedCats[node.id] ? 'open' : ''}`}>›</span>
            )}
            {node.name}
          </button>

          {node.children.length > 0 && expandedCats[node.id] && (
            <CategoryTree
              nodes={node.children}
              selectedCategory={selectedCategory}
              expandedCats={expandedCats}
              onSelect={onSelect}
              onToggle={onToggle}
              depth={depth + 1}
            />
          )}
        </div>
      ))}
    </>
  );
}

const PAGE_SIZE = 40;

function Products() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [products,    setProducts]    = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [loading,      setLoading]     = useState(true);
  const [totalCount,   setTotalCount]  = useState(0);
  const [expandedCats, setExpandedCats] = useState({});

  const rawCat           = searchParams.get('category_id') || searchParams.get('cat');
  const selectedCategory = rawCat ? parseInt(rawCat) : null;
  const searchQuery      = searchParams.get('q') || '';
  const page             = parseInt(searchParams.get('page') || '1');

  const [inputValue, setInputValue] = useState(searchQuery);
  useEffect(() => { setInputValue(searchQuery); }, [searchQuery]);

  useEffect(() => {
    categoryAPI.getCategories().then(r => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedCategory || categories.length === 0) return;
    const cat = categories.find(c => c.id === selectedCategory);
    if (!cat) return;
    if (cat.parent_id) {
      setExpandedCats(prev => ({ ...prev, [cat.parent_id]: true }));
    } else {
      setExpandedCats(prev => ({ ...prev, [cat.id]: true }));
    }
  }, [selectedCategory, categories]);

  useEffect(() => {
    setLoading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const skip = (page - 1) * PAGE_SIZE;
    if (searchQuery) {
      productAPI.searchProducts(searchQuery)
        .then(r => { setProducts(r.data || []); setTotalCount(r.data?.length || 0); })
        .catch(() => { setProducts([]); setTotalCount(0); })
        .finally(() => setLoading(false));
    } else {
      Promise.all([
        productAPI.getProducts(selectedCategory || null, skip, PAGE_SIZE),
        productAPI.countProducts(selectedCategory || null),
      ])
        .then(([prodRes, countRes]) => {
          setProducts(prodRes.data || []);
          setTotalCount(countRes.data?.total || 0);
        })
        .catch(() => { setProducts([]); setTotalCount(0); })
        .finally(() => setLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const goToPage = (p) => {
    const params = new URLSearchParams(searchParams);
    if (p === 1) params.delete('page');
    else params.set('page', p);
    navigate(`/products?${params.toString()}`);
  };

  const handleToggle = (id) => setExpandedCats(prev => ({ ...prev, [id]: !prev[id] }));

  const tree = buildTree(categories);

  return (
    <div className="products-page">
      <div className="container">
        <div className="products-page-header">
          <h1>{selectedCategory ? (categories.find(c => c.id === selectedCategory)?.name || 'All Products') : 'All Products'}</h1>
          <p>{selectedCategory ? `Browse all ${categories.find(c => c.id === selectedCategory)?.name || ''} products` : 'Explore our full catalogue'}</p>
        </div>

        <form className="search-bar" onSubmit={e => { e.preventDefault(); navigate(inputValue ? `/products?q=${encodeURIComponent(inputValue)}` : '/products'); }}>
          <FiSearch size={18} color="var(--text-muted)" />
          <input
            type="text"
            placeholder="Search products…"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => { setInputValue(''); navigate('/products'); }}
            >
              Clear
            </button>
          )}
        </form>

        <div className="products-layout">
          <aside className="sidebar">
            <div className="filter-section">
              <h3>Categories</h3>
              <div className="category-list">
                <button
                  className={`category-btn ${selectedCategory === null ? 'active' : ''}`}
                  onClick={() => navigate('/products')}
                >
                  All Products
                </button>

                <CategoryTree
                  nodes={tree}
                  selectedCategory={selectedCategory}
                  expandedCats={expandedCats}
                  onSelect={(id) => navigate(`/products?category_id=${id}`)}
                  onToggle={handleToggle}
                  depth={0}
                />
              </div>
            </div>
          </aside>

          <main className="products-main">
            <div className="products-toolbar">
              <span className="products-count">
                {loading ? 'Loading…' : searchQuery ? `${products.length} results` : `${totalCount} products`}
              </span>
            </div>

            {loading ? (
              <div className="loading"><div className="spinner" /></div>
            ) : products.length === 0 ? (
              <div className="no-products"><p>No products found</p></div>
            ) : (
              <>
                <div className="grid grid-cols-4 products-grid">
                  {products.map((p, i) => (
                    <div key={p.id} className="product-grid-item" style={{ '--i': i }}>
                      <ProductCard product={p} simple />
                    </div>
                  ))}
                </div>

                {(() => {
                  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
                  if (totalPages <= 1) return null;
                  const btnStyle = (active, disabled) => ({
                    minWidth: 40, height: 40, borderRadius: 8, border: `1.5px solid ${active ? '#111' : '#e2e8f0'}`,
                    background: active ? '#111' : disabled ? '#f8fafc' : '#fff',
                    cursor: disabled ? 'default' : 'pointer',
                    fontWeight: 700, fontSize: '.875rem',
                    color: active ? '#fff' : disabled ? '#94a3b8' : '#1e293b',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 12px',
                  });
                  // Show at most 7 page buttons around current page
                  const pages = [];
                  for (let i = 1; i <= totalPages; i++) {
                    if (i === 1 || i === totalPages || (i >= page - 2 && i <= page + 2)) {
                      pages.push(i);
                    } else if (pages[pages.length - 1] !== '...') {
                      pages.push('...');
                    }
                  }
                  return (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, padding: '32px 0 16px', flexWrap: 'wrap' }}>
                      <button onClick={() => goToPage(page - 1)} disabled={page === 1} style={btnStyle(false, page === 1)}>← Prev</button>
                      {pages.map((p, i) =>
                        p === '...'
                          ? <span key={`d${i}`} style={{ padding: '0 4px', color: '#94a3b8', fontWeight: 700 }}>…</span>
                          : <button key={p} onClick={() => goToPage(p)} style={btnStyle(p === page, false)}>{p}</button>
                      )}
                      <button onClick={() => goToPage(page + 1)} disabled={page === totalPages} style={btnStyle(false, page === totalPages)}>Next →</button>
                    </div>
                  );
                })()}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default Products;
